/**
 * Copyright © Veeam Software Group GmbH.
 */

import { UserRoleRepresentation } from '@veeam-vspc/models/web-controllers';
import { ExportActionIds } from '@veeam-vspc/shared/components';
import { ConfigStates } from '@veeam-vspc/shared/core';

import type { UserConfigData } from '@veeam-vspc/shared/core';

import { ConfigActionIds, ConfigItemIds, ConfigSectionIds } from 'core/enums';
import { RouteIds, RoutePaths } from '../../enums';
import { allCustomRoutes, allHomeRoutes } from '../routes';
import { AppPortalLayout } from 'views/components/layouts/AppPortalLayout';
import { AppPortalBodyLayout } from 'views/components/layouts/AppPortalBodyLayout';

import type { VspcLang } from '../../languages';

export const subtenantConfig: UserConfigData<typeof UserRoleRepresentation.Subtenant, ConfigSectionIds> = {
    role: UserRoleRepresentation.Subtenant,
    routes: [
        allCustomRoutes[RouteIds.Login],
        {
            path: '',
            component: AppPortalLayout,
            items: [
                {
                    path: RoutePaths.Home,
                    component: AppPortalBodyLayout,
                    items: [
                        allHomeRoutes[RouteIds.ActiveAlarms],
                        {
                            ...allHomeRoutes[RouteIds.BillingSummary],
                            navNameKey: 'RESOURCES' as VspcLang['RESOURCES'],
                        },
                        allHomeRoutes[RouteIds.BackupJobs],
                        allHomeRoutes[RouteIds.ProtectedData],
                    ],
                },
            ],
        },
        allCustomRoutes[RouteIds.Api],
        {
            path: '*',
            redirectTo: RoutePaths.NotFound,
        },
    ],
    sections: [
        {
            id: ConfigSectionIds.BackupJobsComputersManagedByConsole,
            columns: {
                companyName: ConfigStates.Hidden,
                siteName: ConfigStates.Hidden,
            },
            defaultActionsState: ConfigStates.Hidden,
        },
        {
            id: ConfigSectionIds.BackupJobsComputersManagedByBackupServer,
            columns: {
                companyName: ConfigStates.Hidden,
                siteName: ConfigStates.Hidden,
            },
        },
        {
            id: ConfigSectionIds.BackupJobsFileSharesLocal,
            columns: {
                companyName: ConfigStates.Hidden,
                siteName: ConfigStates.Hidden,
            },
        },
        {
            id: ConfigSectionIds.BackupJobsDataBackupObjectStorage,
            columns: {
                companyName: ConfigStates.Hidden,
                siteName: ConfigStates.Hidden,
            },
        },
        {
            id: ConfigSectionIds.ProtectedDataComputers,
            columns: {
                companyName: ConfigStates.Hidden,
                siteName: ConfigStates.Hidden,
            },
        },
        {
            id: ConfigSectionIds.ProtectedDataFileSharesLocal,
            columns: {
                companyName: ConfigStates.Hidden,
                siteName: ConfigStates.Hidden,
            },
        },
        {
            id: ConfigSectionIds.ProtectedDataDataBackupObjectStorage,
            columns: {
                companyName: ConfigStates.Hidden,
                siteName: ConfigStates.Hidden,
            },
        },
        {
            id: ConfigSectionIds.ReportsAllReports,
            defaultActionsState: ConfigStates.Hidden,
            actions: {
                [ConfigActionIds.ActionToolbarViewReports]: ConfigStates.Visible,
            },
        },
        {
            id: ConfigSectionIds.InvoicesConfiguration,
            actions: {
                [ConfigActionIds.FilterToolbarType]: ConfigStates.Hidden,
            },
        },
        {
            id: ConfigSectionIds.InvoicesAllInvoices,
            actions: {
                [ConfigActionIds.FilterToolbarType]: ConfigStates.Hidden,
            },
            columns: {
                companyName: ConfigStates.Hidden,
            },
        },
        {
            id: ConfigSectionIds.ActiveAlarmsAlarms,
            defaultActionsState: ConfigStates.Hidden,
            actions: {
                [ExportActionIds.ExportFillSpace]: ConfigStates.Visible,
                [ExportActionIds.ExportToGroup]: ConfigStates.Visible,
                [ExportActionIds.ExportCSV]: ConfigStates.Visible,
                [ExportActionIds.ExportXML]: ConfigStates.Visible,
            },
            columns: {
                companyName: ConfigStates.Hidden,
                siteName: ConfigStates.Hidden,
                regionName: ConfigStates.Hidden,
            },
        },
        {
            id: ConfigSectionIds.ActiveAlarmsAlarmsFilterToolbar,
            actions: {
                [ConfigActionIds.FilterToolbarAdvanced]: ConfigStates.Hidden,
                [ConfigActionIds.FilterToolbarRegionSearch]: ConfigStates.Hidden,
            },
        },
        {
            id: ConfigSectionIds.BillingSummaryCloudConnectResourcesCloudConnectBackup,
            items: {
                [ConfigItemIds.StorageUsageWidget]: ConfigStates.Hidden,
                [ConfigItemIds.StoredWorkloadsWidget]: ConfigStates.Hidden,
                [ConfigItemIds.DataTransferWidget]: ConfigStates.Hidden,
            },
        },
    ],
};
